import { FC } from 'react';
import { Box, Stack } from '@mui/material';

import { SkeletonBlock } from '@mopla/ui';

/** Rows in the conditions meta block (Kontrollmedium / Vertrieben durch / …). */
const CONDITION_ROWS = 3;

const ConditionRow: FC = () => (
	<Stack gap={0.5}>
		<SkeletonBlock w={90} h={16} r={4} />
		<SkeletonBlock w="70%" h={18} r={4} />
	</Stack>
);

/**
 * Loading skeleton for the purchased-ticket detail page. Mirrors the resolved
 * layout — notch card, conditions block, options list — so no layout shift
 * occurs when the real ticket resolves. Rendered inside DTicketPageLayout.
 */
export const MyTicketDetailSkeleton: FC = () => (
	<Stack
		gap={5}
		sx={{ width: '100%', pb: 4 }}
		data-testid="my-ticket-detail-skeleton"
	>
		{/* Operator banner — centered narrow placeholder, 60px reserve (Figma strip ≈59px) */}
		<Box
			sx={{
				mx: '14px',
				pt: '8px',
				height: 60,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
			}}
		>
			<SkeletonBlock w="80%" h={54} r={8} />
		</Box>

		{/* Notch card */}
		<Box
			sx={{
				borderRadius: '16px',
				border: (theme) => `0.5px solid ${theme.palette.primary[200]}`,
				boxShadow:
					'-1px 13px 6px -4px rgba(24, 39, 75, 0.08), 0 3px 16px 4px rgba(24, 39, 75, 0.08)',
				pt: '14px',
				px: 2,
				pb: 2,
			}}
		>
			{/* Header: logo + price/vat */}
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="flex-end"
			>
				<SkeletonBlock w={98} h={54} r={8} />
				<Stack gap={0.5} alignItems="flex-end">
					<SkeletonBlock w={60} h={24} r={4} />
					<SkeletonBlock w={130} h={16} r={4} />
				</Stack>
			</Stack>

			{/* Separator */}
			<Box sx={{ my: 2 }}>
				<SkeletonBlock w="100%" h={1} r={0} />
			</Box>

			{/* Name + status */}
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				gap={1}
			>
				<SkeletonBlock w={190} h={27} r={4} />
				<SkeletonBlock w={84} h={26} r={13} />
			</Stack>

			{/* Validity + remaining */}
			<Stack gap={0.5} sx={{ mt: 1.5 }}>
				<SkeletonBlock w={64} h={21} r={4} />
				<SkeletonBlock w={200} h={24} r={4} />
				<SkeletonBlock w={150} h={18} r={4} />
			</Stack>

			{/* Zones row */}
			<Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
				<SkeletonBlock w={47} h={24} r={4} />
				<SkeletonBlock w={32} h={32} r={16} />
				<SkeletonBlock w={32} h={32} r={16} />
			</Stack>

			{/* Secret-word badge */}
			<Box sx={{ mx: 3, mt: 2 }}>
				<SkeletonBlock w="100%" h={72} r={8} />
			</Box>

			{/* Ticket-ID + operator logo */}
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				sx={{ mt: 2 }}
			>
				<Stack gap={0.5}>
					<SkeletonBlock w={58} h={21} r={4} />
					<SkeletonBlock w={220} h={24} r={4} />
				</Stack>
				<SkeletonBlock w={48} h={48} r={24} />
			</Stack>
		</Box>

		{/* Conditions meta block */}
		<Stack gap={2} sx={{ width: '100%' }}>
			{Array.from({ length: CONDITION_ROWS }).map((_, i) => (
				<ConditionRow key={i} />
			))}
		</Stack>

		{/* Weitere Optionen */}
		<Stack gap={2} sx={{ width: '100%' }}>
			<Stack alignItems="center">
				<SkeletonBlock w={160} h={27} r={4} />
			</Stack>
			<SkeletonBlock w="100%" h={64} r={8} />
			<SkeletonBlock w="100%" h={64} r={8} />
		</Stack>
	</Stack>
);
